import router from "../router";
/* eslint comma-dangle: "off" */
import { defaultLocale } from "../config/i18n";

export const AuthUtils = {
  /**
   * Get current locale from route or localStorage
   * @returns {string} Current locale
   */
  getCurrentLocale() {
    const routeLocale = router.currentRoute?.params?.locale;
    const storedLocale = localStorage.getItem("locale");
    return routeLocale || storedLocale || defaultLocale || "en";
  },

  /**
   * Navigate to a path with locale preservation
   * @param {string} path - Path to navigate to
   * @param {object} options - Navigation options
   */
  navigateWithLocale(path, options = {}) {
    const locale = this.getCurrentLocale();
    const fullPath = path.startsWith("/")
      ? `/${locale}${path}`
      : `/${locale}/${path}`;

    if (options.replace) {
      router.replace(fullPath);
    } else {
      router.push(fullPath);
    }
  },

  /**
   * Redirect after successful login
   * @param {string} redirectPath - Optional redirect path from query params
   */
  redirectAfterLogin(redirectPath = null) {
    const locale = this.getCurrentLocale();
    const defaultPath = `/${locale}/dashboard`;
    const targetPath = redirectPath || defaultPath;

    router.push(targetPath);
  },

  /**
   * Redirect to login page
   * @param {string} currentPath - Current path to redirect back to after login
   */
  redirectToLogin(currentPath = null) {
    const locale = this.getCurrentLocale();
    let loginPath = `/${locale}/auth/login`;

    if (currentPath) {
      loginPath += `?redirect=${encodeURIComponent(currentPath)}`;
    }

    router.push(loginPath);
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    return !!this.getToken();
  },

  /**
   * Get authentication token
   * @returns {string|null} Token or null
   */
  getToken() {
    return localStorage.getItem("auth_token");
  },

  /**
   * Set authentication token
   * @param {string} token - Authentication token
   */
  setToken(token) {
    localStorage.setItem("auth_token", token);
    localStorage.setItem("access_token", token); // For API compatibility
  },

  /**
   * Remove authentication token
   */
  removeToken() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("access_token"); // Remove API compatibility token too
    sessionStorage.removeItem("auth_token");
    sessionStorage.removeItem("access_token");
  },

  /**
   * Clear all authentication data
   */
  clearAuthData() {
    this.removeToken();
    // Clear any other auth-related data as needed
  },
};

export default AuthUtils;
